import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import UserTable from '../components/admin/UserTable';
import UserProfileView from '../components/admin/UserProfileView';
import IncompleteProfilesTables from '../components/admin/IncompleteProfilesTables';
import { AdminListUser, FunnelStatus } from '../types/adminUser';

function matchesUserSearch(user: AdminListUser, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [user.name, user.email, user.companyName, user.country, user.codeName, user.id].some(
    value => (value || '').toLowerCase().includes(q)
  );
}

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminListUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserType, setSelectedUserType] = useState<'startup' | 'investor' | null>(null);
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'completed' | 'incomplete' | 'pending-approval'>(
    searchParams.get('tab') === 'pending-approval' ? 'pending-approval' : 'completed'
  );
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        const listUsersFunction = httpsCallable(functions, 'listUsers');
        const result = await listUsersFunction({});
        const data = result.data as { success?: boolean; users?: AdminListUser[]; error?: string };

        if (data?.success && data.users) {
          const parseDate = (dateValue: unknown): Date | null => {
            if (!dateValue) return null;

            if (dateValue && typeof dateValue === 'object') {
              const rec = dateValue as {
                _seconds?: number;
                _nanoseconds?: number;
                seconds?: number;
                nanoseconds?: number;
                toDate?: () => Date;
              };
              if (rec._seconds !== undefined) {
                return new Date(rec._seconds * 1000 + (rec._nanoseconds || 0) / 1_000_000);
              }
              if (rec.seconds !== undefined) {
                return new Date(rec.seconds * 1000 + (rec.nanoseconds || 0) / 1_000_000);
              }
              if (rec.toDate && typeof rec.toDate === 'function') {
                try {
                  return rec.toDate();
                } catch {
                  return null;
                }
              }
            }

            if (typeof dateValue === 'string') {
              const date = new Date(dateValue);
              return Number.isNaN(date.getTime()) ? null : date;
            }

            if (dateValue instanceof Date) {
              return Number.isNaN(dateValue.getTime()) ? null : dateValue;
            }

            return null;
          };

          const usersWithDates = data.users.map(user => ({
            ...user,
            createdAt: parseDate(user.createdAt),
            lastLoginAt: parseDate(user.lastLoginAt),
            approvedAt: parseDate(user.approvedAt),
            hearAboutUs: user.hearAboutUs || '—',
            plan: user.plan || '—',
            codeName: user.codeName || '—',
            funnelStatus: (user.funnelStatus || 'Abandoned') as FunnelStatus,
            reachedPricingPage: user.reachedPricingPage !== false,
            reachedStripePage: Boolean(user.reachedStripePage),
            passwordSet: user.passwordSet === true || user.isPreview === false,
            emailVerified: user.emailVerified === true,
            expressSignup: user.expressSignup === true,
            smsStatus: user.smsStatus || '—',
            profileExists: user.profileExists === true,
            profileCompleted: user.profileCompleted === true,
            approved: user.approved === true,
            companyName: user.companyName || '',
            thumbnailUrl: user.thumbnailUrl || '',
            currentStep: user.currentStep || 0,
            lastCompletedStep: user.lastCompletedStep ?? -1,
            startupPages: Array.isArray(user.startupPages) ? user.startupPages : [],
          }));
          setUsers(usersWithDates);
        } else {
          setError(data?.error || 'Failed to load users');
        }
      } catch (err: unknown) {
        console.error('Error loading users:', err);
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel('prairi-admin');
    channel.onmessage = event => {
      if (event.data?.type === 'USER_APPROVED') {
        const approvedId = event.data.userId as string;
        setUsers(prev => prev.map(u => (u.id === approvedId ? { ...u, approved: true } : u)));
      }
    };
    return () => channel.close();
  }, []);

  const handleUserClick = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUserId(userId);
      setSelectedUserType(user.userType);
    }
  };

  const handleCloseProfile = () => {
    setSelectedUserId(null);
    setSelectedUserType(null);
  };

  if (selectedUserId && selectedUserType) {
    return (
      <UserProfileView
        userId={selectedUserId}
        userType={selectedUserType}
        onClose={handleCloseProfile}
        hideHeaderClose={selectedUserType === 'investor'}
      />
    );
  }

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#262626' }}
      >
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#262626' }}
      >
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const completedUsers = users.filter(user => user.profileExists && user.profileCompleted);
  const incompleteUsers = users.filter(user => !user.profileExists || !user.profileCompleted);
  const pendingApprovalUsers = users.filter(
    user => user.profileExists && user.profileCompleted && !user.approved
  );
  const filteredCompletedUsers = completedUsers.filter(user =>
    matchesUserSearch(user, searchQuery)
  );
  const filteredIncompleteUsers = incompleteUsers.filter(user =>
    matchesUserSearch(user, searchQuery)
  );
  const filteredPendingUsers = pendingApprovalUsers.filter(user =>
    matchesUserSearch(user, searchQuery)
  );
  const hasSearch = searchQuery.trim().length > 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#262626' }}>
      <div className="container mx-auto px-4 lg:px-8 py-8">
        <div className="mb-4 flex justify-between">
          <button
            onClick={() => navigate('/admindashboard')}
            className="text-sm text-gray-400 hover:text-white transition-colors"
          >
            ← Admin
          </button>
          <button
            onClick={() => navigate('/invitations')}
            className="text-sm text-gray-400 hover:text-white transition-colors"
          >
            Invitation Codes →
          </button>
        </div>

        <div className="mb-4 text-center">
          <h1 className="text-xl text-gray-500 uppercase">Users</h1>
          <p className="text-gray-400 mt-2">
            Total: {users.length} | Completed: {completedUsers.length} | Incomplete:{' '}
            {incompleteUsers.length} | Pending: {pendingApprovalUsers.length}
          </p>
        </div>

        <div className="flex justify-center gap-3 mb-6">
          <button
            onClick={() => setViewMode('completed')}
            className={`px-4 py-2 rounded-md border transition-all duration-200 ${
              viewMode === 'completed'
                ? 'border-emerald-500 text-white'
                : 'border-gray-500 text-gray-300 hover:text-white'
            }`}
          >
            View Completed profiles
          </button>
          <button
            onClick={() => setViewMode('incomplete')}
            className={`px-4 py-2 rounded-md border transition-all duration-200 ${
              viewMode === 'incomplete'
                ? 'border-emerald-500 text-white'
                : 'border-gray-500 text-gray-300 hover:text-white'
            }`}
          >
            View Incomplete profiles
          </button>
          <button
            onClick={() => setViewMode('pending-approval')}
            className={`px-4 py-2 rounded-md border transition-all duration-200 ${
              viewMode === 'pending-approval'
                ? 'border-emerald-500 text-white'
                : 'border-gray-500 text-gray-300 hover:text-white'
            }`}
          >
            Pending Approval
          </button>
        </div>

        {users.length > 0 ? (
          <div className="mb-6 max-w-xl mx-auto">
            <label htmlFor="admin-users-search" className="sr-only">
              Search profiles
            </label>
            <input
              id="admin-users-search"
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or company…"
              className="w-full rounded-xl border border-gray-600 bg-gray-900/90 px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:border-emerald-500/60 focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
            />
          </div>
        ) : null}

        {users.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <p className="text-gray-400 text-lg">No users found</p>
          </div>
        ) : (
          <div className="grid gap-6 grid-cols-1 min-w-0">
            {viewMode === 'completed' ? (
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white">Completed Profiles</h2>
                  <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-semibold">
                    {hasSearch
                      ? `${filteredCompletedUsers.length} of ${completedUsers.length}`
                      : completedUsers.length}
                  </span>
                </div>
                {completedUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No completed profiles</p>
                  </div>
                ) : filteredCompletedUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No completed profiles match your search</p>
                  </div>
                ) : (
                  <div className="max-h-[70vh] overflow-y-auto">
                    <UserTable
                      users={filteredCompletedUsers}
                      onUserClick={handleUserClick}
                      showProfileColumn={true}
                      isIncomplete={false}
                    />
                  </div>
                )}
              </div>
            ) : null}

            {viewMode === 'incomplete' ? (
              <div className="bg-gray-800 rounded-lg p-6 min-w-0 overflow-hidden">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white">Incomplete Profiles</h2>
                  <span className="px-3 py-1 bg-yellow-500 text-white rounded-full text-sm font-semibold">
                    {hasSearch
                      ? `${filteredIncompleteUsers.length} of ${incompleteUsers.length}`
                      : incompleteUsers.length}
                  </span>
                </div>
                {incompleteUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No incomplete profiles</p>
                  </div>
                ) : filteredIncompleteUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No incomplete profiles match your search</p>
                  </div>
                ) : (
                  <IncompleteProfilesTables users={users} searchQuery={searchQuery} />
                )}
              </div>
            ) : null}

            {viewMode === 'pending-approval' ? (
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white">Pending Approval</h2>
                  <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-semibold">
                    {hasSearch
                      ? `${filteredPendingUsers.length} of ${pendingApprovalUsers.length}`
                      : pendingApprovalUsers.length}
                  </span>
                </div>
                {pendingApprovalUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No profiles pending approval</p>
                  </div>
                ) : filteredPendingUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No pending profiles match your search</p>
                  </div>
                ) : (
                  <div className="max-h-[70vh] overflow-y-auto">
                    <UserTable
                      users={filteredPendingUsers}
                      onUserClick={userId => window.open('/approve/' + userId, '_blank')}
                      showProfileColumn={true}
                      isIncomplete={false}
                      layout="two-column"
                    />
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersPage;
